import { z } from 'zod';
import { CORE_IDS } from './ids.js';

/**
 * Owner-kind discriminator for the host's built-in owner kinds. Plugins
 * that introduce additional owner kinds (e.g. `project` from
 * plugin-projects) register an `OwnerKindContributor` through their
 * `PluginServerDefinition.ownerKindContributors`; the host never
 * enumerates plugin kinds in this enum.
 *
 * The enum is still useful for UI controls (the kind picker on
 * `OwnerSelect`), for cross-tier permissions (model-provider secret
 * scope mapping), and for typed switch statements over the host
 * surface. Surfaces that need to display a plugin-contributed kind
 * resolve the display metadata from the contributor at runtime.
 */
export const OwnerKindSchema = z.enum(['user', 'team', 'organization']);
export type OwnerKind = z.infer<typeof OwnerKindSchema>;

/**
 * Minimal prefix → kind lookup for the host-owned id prefixes. Returns
 * `null` for any prefix the host doesn't recognise — callers wanting to
 * resolve a plugin-contributed owner kind (e.g. `prj_`) consult the
 * `OwnerKindContributorRegistry` instead.
 *
 * `null` is also returned for null/undefined/empty ids (host-tier
 * sentinel rows) and any string without a prefix separator.
 */
export function ownerKindOf(ownerId: string | null | undefined): OwnerKind | null {
  if (!ownerId) return null;
  const idx = ownerId.indexOf('_');
  if (idx < 0) return null;
  const prefix = ownerId.slice(0, idx);
  if (prefix === CORE_IDS.user) return 'user';
  if (prefix === CORE_IDS.team) return 'team';
  if (prefix === CORE_IDS.organization) return 'organization';
  return null;
}

/**
 * Backwards-compatible alias for {@link OwnerKind}. Existing callers
 * that imported `ResourceOwnerType` (e.g. the model-provider-secrets
 * scope-allowance helper) keep working without churn.
 *
 * @deprecated Use {@link OwnerKind}.
 */
export type ResourceOwnerType = OwnerKind;

/**
 * Per-resource owner. A single prefixed id whose kind (`user` / `team` /
 * `organization` / `project` / …) is derived from the id's prefix.
 *
 * Visibility predicate at every call site is the same:
 * `ownerId IS NULL OR ownerId = ANY(callerOwnerIds)`, where
 * `callerOwnerIds = [userId, organizationId, ...teamIds, ...projectIds]`.
 *
 * - `ownerId = <userId>` — visible only to that user (plus elevated).
 * - `ownerId = <teamId>` — visible to members of that team.
 * - `ownerId = <organizationId>` — visible to every member of the org. This
 *    is the explicit "organization" sentinel; the row's `organization_id`
 *    column still scopes tenancy but the owner-scope predicate uses
 *    `ownerId` exclusively.
 * - `ownerId = null` — host-tier rows (pre-multi-tenancy seeds and the
 *    handful of `organization_id IS NULL` configs that pre-date this
 *    refactor). Treated as visible to every authenticated caller.
 */
export const ResourceOwnerSchema = z.object({
  ownerId: z.string().min(1).nullable(),
});
export type ResourceOwner = z.infer<typeof ResourceOwnerSchema>;

/**
 * Client-supplied input on create/update. Mirrors {@link ResourceOwnerSchema}
 * so routes can `parseBody()` it and pass it straight through to services.
 * Services still validate `ownerId` against the caller's user/team/org/project
 * memberships before persisting — schema-level validation only catches
 * malformed shapes, not impersonation.
 */
export const ResourceOwnerInputSchema = ResourceOwnerSchema;
export type ResourceOwnerInput = ResourceOwner;

/**
 * UI list-filter selector. `'all'` opts the user out of any filter (still
 * subject to the underlying visibility rules); the others narrow the result
 * set by ownerId.
 *
 * - `'mine'`          — `ownerId = callerUserId`
 * - `'team'`          — `ownerId = ownerTeamId`
 * - `'organization'`  — `ownerId = callerOrganizationId`
 */
export const ResourceOwnerFilterSchema = z.union([
  z.object({ scope: z.literal('all') }),
  z.object({ scope: z.literal('mine') }),
  z.object({ scope: z.literal('team'), ownerTeamId: z.string().min(1) }),
  z.object({ scope: z.literal('organization') }),
]);
export type ResourceOwnerFilter = z.infer<typeof ResourceOwnerFilterSchema>;

/**
 * The "mine + team + org" filter scopes a list page can use without
 * needing to know about specific team ids — useful for URL state.
 */
export const ResourceOwnerFilterScopeSchema = z.enum(['all', 'mine', 'team', 'organization']);
export type ResourceOwnerFilterScope = z.infer<typeof ResourceOwnerFilterScopeSchema>;

/**
 * Default owner applied to a freshly-created resource when the caller
 * doesn't supply one. Owning the resource yourself is the safest default:
 * organization-wide visibility is opt-in, not opt-out.
 */
export function defaultOwnerForUser(userId: string): ResourceOwner {
  return { ownerId: userId };
}
